import "react-app-polyfill/ie11";
import "react-app-polyfill/stable";

import React, { useEffect } from "react";
import ReactDom from "react-dom";
import history from "./services/history";

import "./styles/index.sass";

import { MainView } from "./components/Main";

import { asyncDispatchMiddleware, DispatchContext, initialState, reducer } from "./store";
import { getCurrentMode, parseQueryString, updateFilterParams } from "./utils/queryStringHelper";
import { snippetsEqual } from "./services/snippetService";
import { Mode } from "models";

const App = () => {
    const [store, dispatch] = React.useReducer(reducer, initialState);

    if (document.location.pathname === "/") {
        history.push("basic");
    }

    const keyHandler = (e: KeyboardEvent) => {
        if (e.ctrlKey && e.code === "KeyS") {
            e.preventDefault();
            asyncDispatchMiddleware(dispatch)({ type: "SnippetSaveClick", snippet: store.snippet })
        }
    }

    useEffect(() => {
        asyncDispatchMiddleware(dispatch)({ type: "SnippetsListLoad" });
        remote.api.snippet.Tags()
            .then(tagsArr => {
                const tags = new Map<number, string>(tagsArr
                    .filter(t => !t.hidden)
                    .map(t => [t.id, t.name]));
                asyncDispatchMiddleware(dispatch)({ type: "TagsLoaded", tags });
            });


        window.addEventListener("message", e => {
            if (e.data) {
                if (e.data.type === "snippet-error") {
                    asyncDispatchMiddleware(dispatch)({
                        type: "SnippetErrorOccured",
                        msg: e.data.text || "",
                        line: e.data.line || 0
                    });
                }
                if (e.data.type === "snippet-request-data") {
                    asyncDispatchMiddleware(dispatch)({ type: "PreviewInitialized" });
                }
            }
        });
        asyncDispatchMiddleware(dispatch)({type: "GetSamplesCount"})
    }, []);

    useEffect(() => history.listen(location => {
        const id = location.pathname.split("/")[1];

        const mode = getCurrentMode();
        if (store.mode !== mode && store.mode !== "search") {
            dispatch({ type: "ModeChanged", mode });
        }

        if (id && (!store.snippet || id !== store.snippet.id)) {
            asyncDispatchMiddleware(dispatch)({ type: "SnippetLoadRequested", id });
        }
    }));

    useEffect(() => {
        window.onbeforeunload = (e: BeforeUnloadEvent) => {
            const saved = snippetsEqual(store.snippet, store.savedSnippet);
            if (saved) {
                delete e.returnValue;
                return undefined;
            }
            else {
                e.returnValue = "Your snippet might not be saved!";
                return "Your snippet might not be saved!";
            }
        };
        const listner = (e: KeyboardEvent) => {
            if ((e.ctrlKey || e.metaKey) && e.code === "KeyS") {
              e.preventDefault();
              asyncDispatchMiddleware(dispatch)({ type: "SnippetSaveClick", snippet: store.snippet });
            }
        }
        document.addEventListener("keydown", listner);
        return () => {
            window.onbeforeunload = null;
            document.removeEventListener("keydown", listner);
        };
    });

    useEffect(() => {
        const qs = parseQueryString(location.href.split('?')[1] || "");
        const id = location.pathname.split("/")[1];
        const tag = qs.tag?.length ? qs.tag.reduce((acc, val) => acc += `#${val} `, "") : "";
        const text = `${tag} ${qs.text || ""}`.trim();
        if (id === "all") {
            asyncDispatchMiddleware(dispatch)({ type: "SearchClick"});
            return
        }
        if (id && (!store.snippet || store.snippet.id !== id)) {
            asyncDispatchMiddleware(dispatch)({ type: "SnippetLoadRequested", id });
        }
        if (qs.mode || qs.text || qs.tag) {
            dispatch({type: "SetParamsFromQuerySearch", text: qs.text || "", tag: qs.tag})
        }
        const mode = getCurrentMode();
        if (store.mode !== mode && store.mode !== "search") {
            dispatch({ type: "ModeChanged", mode });
        }
        asyncDispatchMiddleware(dispatch)({ type: "SearchPhraseChanged", text, scrollToSnippet: true });
    }, []);

    useEffect(() => {
        const id = location.pathname.split("/")[1];
        if (id === "all" && !store.snippet.id && store.searchResults.length) {
            asyncDispatchMiddleware(dispatch)({ type: "SearchSnippetSelected", id: store.searchResults[0].url })
        }
    }, [store.searchResults]);
    

    return <DispatchContext.Provider value={{ dispatch: asyncDispatchMiddleware(dispatch) }}>
        <MainView state={store}/>
    </DispatchContext.Provider>;
};

ReactDom.render(<App />, document.getElementById("root"));
